import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import Alerts from 'components/UI/Alerts/Alerts';
import ValidationAlert from 'components/UI/Alerts/ValidationAlert/ValidationAlert';
import { resolveValidationColor } from 'components/UI/Alerts/ValidationAlert/validationAlertUtils';
import Automation from './Automation/Automation';
import OcrVerificationChip from './OcrVerificationChip/OcrVerificationChip';
import FieldSourceIcon from './FieldSourceIcon/FieldSourceIcon';
import VerifyCheckmark from 'assets/icons/VerifyCheckmark';
import Input from './Input/Input';
import InventoryStatus from './InventoryStatus';
import Label from './Label/Label';
import { AUTOMATION_SOURCE, INVENTORY_STATUS } from 'utils/constants';
import { FENIX_EXPORTED_TO_YMS_FIELD } from 'utils/fenixYms';
import { getSheetAnnouncementMessage } from '../../../../lib/sheetAnnouncement';
import { useProtocolAutomation } from 'features/stepByStepProtocol';
import styles from './AIQInput.module.css';

let componentId = 1;

function AIQInput(props) {
    const {
        label,
        automation: formAutomation,
        backgroundColor,
        borderColor,
        disabled,
        error,
        helperText,
        forceAutomationStyles,
        name,
        option,
        issues,
        transactionId,
        camInfo,
        inputContainerClasses,
        isInventoryProperty,
        fieldValueSource,
        persistedValidationMap,
        sheetAnnouncementValue,
        ocrChip,
    } = props;

    const conditionalFieldValidation = persistedValidationMap?.[name];
    const conditionalValidationStatus = conditionalFieldValidation?.status;
    const conditionalValidationMessage = conditionalFieldValidation?.message;
    const conditionalValidationColor = conditionalFieldValidation?.color;
    const hasAutofill = conditionalFieldValidation?.hasAutofill ?? false;
    const isSplitView = props.isSplitView ?? false;
    const isAlisaSource = fieldValueSource === AUTOMATION_SOURCE.AI_OCR;
    const isSheetAnnouncementSource = fieldValueSource === AUTOMATION_SOURCE.SHEET_ANNOUNCEMENT;
    const sheetAnnouncementData = sheetAnnouncementValue || {};
    const sheetAnnouncementDisplayValue = sheetAnnouncementData?.value;
    const sheetAnnouncementStatus = sheetAnnouncementData?.status;
    const sheetAnnouncementColor = sheetAnnouncementData?.color ?? null;
    const hasSheetAnnouncementValue = isSheetAnnouncementSource && sheetAnnouncementDisplayValue != null && sheetAnnouncementDisplayValue !== '';
    const activeValidation = hasSheetAnnouncementValue
        ? {
              status: sheetAnnouncementStatus,
              message: getSheetAnnouncementMessage(sheetAnnouncementStatus, sheetAnnouncementDisplayValue),
              color: sheetAnnouncementColor,
              isSheetAnnouncement: true,
          }
        : conditionalValidationStatus
          ? {
                status: conditionalValidationStatus,
                message: conditionalValidationMessage,
                color: conditionalValidationColor,
                hasAutofill,
            }
          : null;
    const validationColor = activeValidation ? resolveValidationColor(activeValidation.color, activeValidation.status) : null;
    const isAutoSource = fieldValueSource === AUTOMATION_SOURCE.AUTO;
    const showSourceIcon = isSplitView && !!fieldValueSource && !isAutoSource;
    const showCheckmark = isSplitView && isAlisaSource && !disabled;
    const showOrangeDivider = isSplitView && isAlisaSource;
    const showAutoLabel = isSplitView && isAutoSource;
    const autoTooltip = name === FENIX_EXPORTED_TO_YMS_FIELD ? 'Auto (auto-filled)' : 'Auto (timer-derived)';

    // A step-by-step protocol `/` suggestion has the lowest priority: it shows only when the field has no
    // other automation.
    const protocolAutomation = useProtocolAutomation(transactionId, name);
    const automation = formAutomation && Object.keys(formAutomation).length ? formAutomation : (protocolAutomation ?? formAutomation);
    // A fresh <Automation> when the chip switches between the Protocol `/` and the field's own automation,
    // so the first real automation after the Protocol one is not styled as an overwritten one.
    const automationKey = automation === protocolAutomation ? 'protocol' : 'form';

    const [focused, setFocused] = useState(false);
    const [id] = useState(componentId++);
    const hasAutomation =
        !isSplitView && (!!ocrChip || (automation && Object.keys(automation).length) || (forceAutomationStyles && name !== 'sendToBison')); //TODO do not hardcode, ask for flags from BE
    const containerRef = useRef();
    const labelRef = useRef();
    const dispatch = useDispatch();

    const inventoryStatus = option?.inventoryStatus || '';

    useEffect(() => {
        const { current: containerElement } = containerRef;
        const { current: labelElement } = labelRef;

        if (backgroundColor) {
            containerElement.style.setProperty('--aiq-bg-color', backgroundColor);
            if (!isSplitView) labelElement.style.outline = `1px solid ${backgroundColor}`;
            // labelElement.style.color = mainColor;
        }
        if (borderColor) {
            containerElement.style.setProperty('--aiq-border-color', borderColor);
            if (!isSplitView) {
                labelElement.style.backgroundColor = borderColor;
                labelElement.style.color = 'white';
            }
        }
        if (validationColor) containerElement.style.setProperty('--aiq-validation-color', validationColor);
        if (isInventoryProperty && inventoryStatus) {
            const statusColor = inventoryStatus === INVENTORY_STATUS.GOOD_TO_GO ? '#AEE9C1' : '#ef4444';
            const shouldShowBg = !isSplitView || inventoryStatus !== INVENTORY_STATUS.GOOD_TO_GO;

            if (shouldShowBg) {
                containerElement.style.setProperty('--aiq-bg-color', statusColor);
            }
            if (!isSplitView) labelElement.style.outline = `1px solid ${statusColor}`;
        }
        return () => {
            containerElement.style.removeProperty('--aiq-bg-color');
            containerElement.style.removeProperty('--aiq-border-color');
            containerElement.style.removeProperty('--aiq-validation-color');
            if (!isSplitView) labelElement?.removeAttribute('style');
        };
    }, [backgroundColor, borderColor, isInventoryProperty, inventoryStatus, isSplitView, validationColor]);

    const containerStyles = getContainerStyles({
        inputContainerClasses,
        focused,
        hasAutomation,
        disabled,
        error,
        hasValidation: !!activeValidation,
        isSplitView,
        name,
    });

    const inputStyles = `${styles.input}${backgroundColor || hasAutomation ? ` ${styles['input--border-color']}` : ''}`;

    const clickHandlerTransactionBugIcon = (state) => {
        dispatch(transactionActions.setReportIssuesModalState(state));
        dispatch(transactionActions.setSelectedCameraIssues(issues));
        dispatch(transactionActions.setSelectedIssueUrl(`/transactions/trucks/${transactionId}/issue`));
        dispatch(transactionActions.setIssueTitle(issues[0]?.issueType?.issueName));
        dispatch(transactionActions.setIssueDataLoggs({ ...camInfo, fieldName: props.name }));
    };

    const alerts = option?.alerts || option?.equipmentAlerts;
    const alertMode = alerts ? 'hover' : 'default';

    const handleVerifyCheckmark = useCallback(() => {
        dispatch(formControlActions.markFieldUserEdited({ formId: transactionId, fieldName: name }));
    }, [dispatch, transactionId, name]);

    const handleContainerClick = useCallback((e) => {
        if (e.target.closest(`.${styles['verify-checkmark']}`)) return;
        const input = containerRef.current?.querySelector('input');
        if (input && !input.disabled) input.focus();
    }, []);

    return (
        <div
            // The field schema key, for the activity focus tracker (BEAST-13036). There is no other
            // path from a DOM focus event back to the form config's `dataField`: MUI and DevExtreme
            // generate their own `id` and `name`, so the tracker resolves this attribute with
            // `closest('[data-field-name]')` instead. Never a value, only the key.
            data-field-name={name}
            className={`${styles['wrapper-container']}${isSplitView ? ` ${styles['wrapper-container--splitView']}` : ''}`}
        >
            <div className={containerStyles} ref={containerRef} onClick={isSplitView ? handleContainerClick : undefined}>
                {label && !isSplitView && <Label {...{ id, label, focused, error }} ref={labelRef} />}
                {isSplitView && (
                    <div className={styles['splitView-label-row']}>
                        {label && (
                            <span className={styles['splitView-label']} ref={labelRef}>
                                {label}
                            </span>
                        )}
                        {ocrChip ? (
                            <OcrVerificationChip chip={ocrChip} className={styles['ocr-chip--splitView']} />
                        ) : (
                            showSourceIcon && <FieldSourceIcon source={fieldValueSource} />
                        )}
                        {showAutoLabel && (
                            <span className={styles['auto-suggestion-chip']} title={autoTooltip}>
                                <span className={styles['auto-suggestion-chip__source']}>Auto</span>
                            </span>
                        )}
                    </div>
                )}

                <div className={styles['alerts-row']}>
                    <Alerts alerts={alerts} containerClasses={styles['alert-container--inline']} mode={alertMode} />
                    {activeValidation && <ValidationAlert {...activeValidation} />}
                </div>
                {isInventoryProperty && inventoryStatus && <InventoryStatus option={option} />}
                {ocrChip && !isSplitView && (
                    <div className={styles['ocr-chip-container']}>
                        <OcrVerificationChip chip={ocrChip} />
                    </div>
                )}
                {!isSplitView && (
                    <div
                        className={`${styles['issue-icon-automation-container']}${ocrChip ? ` ${styles['issue-icon-automation-container--ocr']}` : ''}`}
                        onMouseDownCapture={(e) => {
                            e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {/* the OCR chip shows the ALISA values of a verified field, so it takes the automation chip's place */}
                        <Automation
                            key={automationKey}
                            disabled={disabled}
                            automation={ocrChip ? undefined : automation}
                            issues={issues}
                            autoTooltip={autoTooltip}
                            onBugIconClick={clickHandlerTransactionBugIcon}
                        />
                    </div>
                )}
                <Input {...{ inputStyles, setFocused, id, focused, ...props, ...(isSplitView && { dropdownArrow: false }) }} />
                {showCheckmark && (
                    <div className={styles['verify-checkmark']}>
                        <VerifyCheckmark onClick={handleVerifyCheckmark} />
                    </div>
                )}
                {showOrangeDivider && <div className={styles['automation-divider']} />}
            </div>
            {helperText && (
                <p className={styles['helper-text']} title={isSplitView ? helperText : undefined}>
                    {helperText}
                </p>
            )}
        </div>
    );
}

export default React.memo(AIQInput);

function getContainerStyles({ focused, hasAutomation, disabled, error, hasValidation, inputContainerClasses, isSplitView, name }) {
    const isAnnouncement = name?.startsWith('announcement');
    return `${styles['input-container']}${inputContainerClasses ? ` ${inputContainerClasses}` : ''}${
        focused ? ` ${styles['input-container--focus']}` : ''
    }${hasAutomation ? '' : ` ${styles['input-container--input-only']}`}${disabled ? '' : ` ${styles['input-container--standard']}`}${
        error
            ? focused
                ? ` ${styles['input-container--error']} ${styles['input-container--focus--error']}`
                : ` ${styles['input-container--error']}`
            : ''
    }${hasValidation ? ` ${styles['input-container--validation']}` : ''}${isSplitView ? ` ${styles['input-container--splitView']}` : ''}${
        isAnnouncement ? ` ${styles['input-container--announcement']}` : ''
    }`;
}
